import { deviceUtils } from 'scbk-services';
import { useNativeBridge } from './useNativeBridge';

export namespace App {
    export interface DoNAutoLogoutRequest {
        type: '0' | '1';
        remainTime?: number;
    }

    export interface DoNAutoLogoutResponse {
        type: '0' | '1';
        result: '0' | '1';
    }
}
/**
 * APP 관련 네이티브 브릿지 함수모음 hook
 * @returns
 */
const useAppBridge = () => {
    const nativeBridge = useNativeBridge();

    /**
     * 프로그래스 다이얼로그 열기
     */
    const doNOpenProgressDlg = async () => {
        nativeBridge('doNOpenProgressDlg', undefined, { onlyRequest: true });
    };

    /**
     * 프로그래스 다이얼로그 닫기
     */
    const doNCloseProgressDlg = async () => {
        nativeBridge('doNCloseProgressDlg', undefined, { onlyRequest: true });
    };

    /**
     *
     * @param screenValue
     */
    const doNScreenControl = async (screenValue: 'Y' | 'N') => {
        nativeBridge('doNScreenControl', { screenValue }, { onlyRequest: true });
    };

    /**
     * 앱 종료
     */
    const doNAppExit = async () => {
        nativeBridge('doNAppExit', undefined, { onlyRequest: true });
    };

    /**
     * 로그인종류 전달
     * @param type 0:Logout 1:ID/PWD 2:InisafeCert 3:UbiKeyCert 4:UsimCert 5:BankSign 6:FIDO 7:금융인증서 8:핀테크인증서 9:모바일OTP
     */
    const doNLoginStatus = async (
        type: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9',
    ) => {
        nativeBridge('doNLoginStatus', { type }, { onlyRequest: true });
    };

    /**
     *언어설정 set
     * @param locale en | ko
     */
    const doNSetLocale = async (locale: 'en' | 'ko') => {
        nativeBridge('doNSetLocale', { locale }, { onlyRequest: true });
    };

    /**
     * 언어설정 get
     * @returns
     */
    const doNGetLocale = async (): Promise<string | undefined> => {
        const res = await nativeBridge<unknown, { locale: string }>('doNGetLocale', undefined);
        if (res.status === 'fail') {
            return;
        }

        return res.payload?.locale;
    };

    /**
     * App 스토리지 저장
     * @param group
     * @param key
     * @param value
     */
    const doNSetDeviceStorage = async (group: string, key: string, value: any) => {
        nativeBridge('doNSetDeviceStorage', { group, key, value: encodeURIComponent(value ?? '') }, { onlyRequest: true });
    };

    /**
     * APP 스토리지에 저장된 정보를 가져온다.
     * @param group 그룹
     * @param key 키
     * @returns
     */
    const doNGetDeviceStorage = async (
        group: string,
        key: string,
    ): Promise<unknown | undefined> => {
        const res = await nativeBridge<{ group: string; key: string }, { value: unknown }>(
            'doNGetDeviceStorage',
            { group, key },
        );

        if (res.status === 'fail') {
            return;
        }
        
        const payload = res.payload;
        let value = decodeURIComponent(payload?.value as string ?? '');
        
        if(deviceUtils.isAndroid()) {
            value = decodeURIComponent(value);
        }

        console.log('# doNGetWebviewStorage value=', value);
        return value;
    };

    /**
     * 외부앱 설치여부
     * @param packageName 패키지명
     * @returns
     */
    const doNCheckAppInstall = async (packageName: string) => {
        const res = await nativeBridge<{ packageName: string }, { result: 'Y' | 'N' }>(
            'doNCheckAppInstall',
            { packageName },
        );

        if (res.status === 'fail') {
            return 'N';
        }

        const payload = res.payload;
        if (!payload) {
            return 'N';
        }
        return payload.result;
    };

    /**
     * 앱접근권한 허용 체크 및 요청
     * @param type 0:위치, 1:카메라, 2:주소록, 3:SMS
     * @returns 0:허용됨, 1:거부됨
     */
    const doNPermissionCheck = async (type: '0' | '1' | '2' | '3') => {
        const res = await nativeBridge<{ type: string }, { result: '0' | '1' }>(
            'doNPermissionCheck',
            { type },
        );

        if (res.status === 'fail') {
            return '0';
        }

        const payload = res.payload;
        if (!payload) {
            return '0';
        }
        return payload.result;
    };

    /**
     *
     * @param request
     *   type 0: 연장팝업, 1: 로그아웃팝업
     *   result	0: left, 1; right
     * @returns
     */
    const doNAutoLogout = async (request: App.DoNAutoLogoutRequest) => {
        //[2026.05.20 최성돈] 타임아웃을 -1로 설정하여. 콜백을 계속 기다리도록 한다.
        const res = await nativeBridge<App.DoNAutoLogoutRequest, App.DoNAutoLogoutResponse>(
            'doNAutoLogout',
            request,
            {
                timeoutMillis: -1,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const payload = res.payload;
        if (!payload) {
            return;
        }
        return payload;
    };

    /**
     * 글자색을 변경한다.
     * @param color 0: black, 1: white
     */
    const doNSetStatusBarIconColor = async (color: '0' | '1') => {
        await nativeBridge('doNSetStatusBarIconColor', { color }, { onlyRequest: true });
    };

    /**
     * 외부앱 실행
     * @param packageName 패키지명
     */
    const doNAppExecutePackage = async (packageName: string) => {
        const res = await nativeBridge<{ packageName: string }, { result: 'Y' | 'N' }>(
            'doNAppExecutePackage',
            { packageName },
        );

        if (res.status === 'fail') {
            return;
        }

        const payload = res.payload;
        if (!payload) {
            return;
        }
        return payload.result;
    };

    /**
     * 앱캐시클리어 (AND : 1.4.5 이상, IOS : 1.3.6 이상)
     */
    const doNAppCacheClear = async () => {
        nativeBridge('doNAppCacheClear', {}, { onlyRequest: true });
    };

    /**
     * 앱 리뷰 요청
     */
    const doNReqAppReview = async () => {
        nativeBridge('doNReqAppReview', {}, { onlyRequest: true });
    };

    /**
     * 안드로이드 백그라운드 서비스 활성화 (1:start, 2:end)
     */
    const doNProgBackgroundService = async (status: '1' | '2') => {
        //TODO iOS인경우 return
        if (deviceUtils.isIOS()) {
            return { status: 'success' };
        }

        const res = await nativeBridge<{ status: '1' | '2' }, unknown>('doNProgBackgroundService', {
            status,
        });

        if (res.status === 'fail') {
            return;
        }

        return res.status;
    };

    /**
     * text 공유
     * @param text 공유할 문자열
     */
    const doNTextShare = async (text: any) => {
        nativeBridge('doNTextShare', { text }, { onlyRequest: true });
    };

    /**
     * 웹뷰 스토리지 저장
     * 웹뷰 스토리지에 특정키로 값을 저장한다.
     * @param key
     * @param value
     */
    const doNSetWebviewStorage = async (key: string, value: string) => {
        await nativeBridge('doNSetWebviewStorage', { key, value: encodeURIComponent(value ?? '') });
    };

    /**
     * 웹뷰 스토리지에서 가져오기
     * 웹뷰 스토리지에 특정키로 저장된 값을 가져온다.
     * @param key
     */
    const doNGetWebviewStorage = async (key: string): Promise<string | undefined> => {
        const res = await nativeBridge<{ key: string }, { value: string }>('doNGetWebviewStorage', {
            key,
        });

        if (res.status === 'fail') {
            return;
        }

        const payload = res.payload;
        const value = decodeURIComponent(payload?.value ?? '');
        console.log('# doNGetWebviewStorage value=', value);
        return value;
    };

    /**
     * 웹뷰 스토리지 삭제
     * 웹뷰 스토리지의 특정키로 저장된 데이터를 삭제한다
     * @param key
     */
    const doNRemoveWebviewStorage = async (key: string) => {
        nativeBridge('doNRemoveWebviewStorage', { key }, { onlyRequest: true });
    };

    /**
     * 웹뷰 스토리지 키 목록 조회
     * 웹뷰스토리지에 저장된 키 목록을 가져온다.
     */
    const doNGetWebviewStorageKeys = async () => {
        const res = await nativeBridge<{}, { value: string[] }>('doNGetWebviewStorageKeys');

        if (res.status === 'fail') {
            return;
        }

        const payload = res.payload;
        return payload?.value ?? [];
    };

    /**
     * 웹뷰 스토리지 clear
     * 웹뷰 스토리지를 clear한다.
     * @param key
     */
    const doNClearWebviewStorage = async () => {
        nativeBridge('doNClearWebviewStorage', {}, { onlyRequest: true });
    };

    /**
     * 편한뱅킹 모드 활성/비활성화 처리
     * @param value Y: 편한뱅킹모드, N: 일반뱅킹모드
     */
    const doNSetComfyBanking = async (value: 'Y' | 'N') => {
        nativeBridge('doNSetComfyBanking', { value }, { onlyRequest: true });
    };

    /**
     * 사용자 로그아웃 처리
     * @param clearWebview: 업무웹뷰 정리여부 Y: 정리, N: 정리하지 않음
     */
    const doNLogout = async (clearWebview: 'Y' | 'N') => {
        nativeBridge('doNLogout', { clearWebview }, { onlyRequest: true });
    };

    /**
     * 네이티브 세션타이머 시작
     * @param timeout 세션만료 시간
     */
    const doNStartSessionTimer = async (timeout: number = 600) => {
        nativeBridge('doNStartSessionTimer', { timeout }, { onlyRequest: true });
    };

    /**
     * 네이티브 세션타이머 재시작
     */
    const doNResetSessionTimer = async () => {
        nativeBridge('doNResetSessionTimer', {}, { onlyRequest: true });
    };

    return {
        doNOpenProgressDlg,
        doNCloseProgressDlg,
        doNScreenControl,
        doNAppExit,
        doNLoginStatus,
        doNSetLocale,
        doNGetLocale,
        doNSetDeviceStorage,
        doNGetDeviceStorage,
        doNCheckAppInstall,
        doNPermissionCheck,
        doNAutoLogout,
        doNSetStatusBarIconColor,
        doNAppExecutePackage,
        doNAppCacheClear,
        doNReqAppReview,
        doNProgBackgroundService,
        doNTextShare,
        doNSetWebviewStorage,
        doNGetWebviewStorage,
        doNRemoveWebviewStorage,
        doNGetWebviewStorageKeys,
        doNClearWebviewStorage,
        doNSetComfyBanking,
        doNLogout,
        doNStartSessionTimer,
        doNResetSessionTimer,
    };
};

export { useAppBridge };
